import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { ResourceBundle } from '@waldermarch/shared';

import { api } from '@/utils/api';
import { useNotificationStore } from './notifications';

export interface HeldTitle {
  tier: 'county' | 'duchy' | 'kingdom' | 'empire';
  id: number;
  name: string;
  /** The title one rung up, for the "under which" line. */
  under: string;
}

export interface TitleOffer {
  tier: 'duchy' | 'kingdom' | 'empire';
  id: number;
  name: string;
  under: string;
  held: number;
  total: number;
  needed: number;
  heldBy: { id: string; name: string } | null;
  cost: ResourceBundle | null;
  restingUntil: number | null;
  /** False while the de jure threshold is not yet crossed: a greyed prospect. */
  ready: boolean;
}

/** What each rung calls the lord who holds it. */
const RANK: Record<HeldTitle['tier'], string> = {
  empire: 'Emperor',
  kingdom: 'Ruler',
  duchy: 'Duke',
  county: 'Count',
};

/**
 * The grandest thing the player is called.
 *
 * Counties included, because most players will never hold anything else and a
 * style that goes blank for them is worse than no style at all. A lord with
 * several counties and no duchy is styled for the largest of them.
 */
export function grandestOf(titles: HeldTitle[]): {
  rank: string;
  name: string;
  tier: HeldTitle['tier'];
} | null {
  const top = titles.find((t) => t.tier === 'empire')
    ?? titles.find((t) => t.tier === 'kingdom')
    ?? titles.find((t) => t.tier === 'duchy')
    ?? titles.find((t) => t.tier === 'county');
  if (!top) return null;
  return { rank: RANK[top.tier], name: top.name, tier: top.tier };
}

/**
 * Titles held, and titles on offer.
 *
 * Both are read from the server rather than worked out here, because both are
 * rulings rather than opinions: whether a lord is entitled to a crown depends
 * on who holds eighteen hundred villages, and the client is in no position to
 * know that. What the client does is ask, and draw the answer.
 */
export const useTitlesStore = defineStore('titles', () => {
  const held = ref<HeldTitle[]>([]);
  const counts = ref({ county: 0, duchy: 0, kingdom: 0, empire: 0 });
  const offers = ref<TitleOffer[]>([]);
  const loading = ref(false);
  const taking = ref<string | null>(null);

  const style = computed(() => grandestOf(held.value));

  async function refresh(): Promise<void> {
    loading.value = true;
    try {
      const [mine, decisions] = await Promise.all([
        api.get<{ titles: HeldTitle[]; counts: typeof counts.value }>('/titles/mine'),
        api.get<{ offers: TitleOffer[] }>('/titles/decisions'),
      ]);
      held.value = mine.titles;
      counts.value = mine.counts;
      offers.value = decisions.offers;
    } catch {
      // A failed poll is not worth a notification; the next one will do.
    } finally {
      loading.value = false;
    }
  }

  /** Claim a vacant title, or usurp a worn one, paying from `villageId`. */
  async function take(offer: TitleOffer, villageId?: string): Promise<boolean> {
    const notifications = useNotificationStore();
    taking.value = `${offer.tier}:${offer.id}`;
    try {
      await api.post('/titles/take', { tier: offer.tier, id: offer.id, villageId });
      notifications.success(
        offer.heldBy
          ? `${offer.name} is taken from ${offer.heldBy.name}.`
          : `${offer.name} is yours.`,
      );
      await refresh();
      return true;
    } catch (err) {
      notifications.error(err instanceof Error ? err.message : 'The claim was refused.');
      return false;
    } finally {
      taking.value = null;
    }
  }

  return { held, counts, offers, loading, taking, style, refresh, take };
});
